import { Component, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';

import {
  ONBOARDING_CONFIG,
  getStartOptions,
  getRouteForStartOption,
  StartOptionId,
} from '../onboarding.config';
import { AuthStore } from '@rapider/authentication';

/**
 * @class OnboardingComponent
 * @description The main onboarding screen prompting the user to choose how they wish to start managing their data.
 * Designed to reduce anxiety by providing clear, concrete paths and a fallback reassurance.
 */
@Component({
  selector: 'rapider-onboarding',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './onboarding.component.html',
  host: {
    // Ensures the component takes full viewport height and applies the global body background token
    class:
      'block h-full w-full bg-body-background overflow-y-auto custom-scrollbar',
  },
})
export class OnboardingComponent {
  private readonly router = inject(Router);
  private readonly authStore = inject(AuthStore);

  // Configuration references
  public readonly config = ONBOARDING_CONFIG;

  // Data Signals
  public startOptions = computed(() => {
    const options = getStartOptions();
    const user = this.authStore.user();

    const isAdmin =
      user &&
      user['permissions'] &&
      Array.isArray(user['permissions']) &&
      user['permissions'].includes('admin');

    if (isAdmin) {
      return options;
    }

    return options.filter((option) => option.id !== 'templates');
  });

  public isNavigating = signal<boolean>(false);

  /**
   * Handles the selection of a start option card.
   * Leverages a pure function to determine the target route and navigates.
   * * @param {StartOptionId} optionId - The identifier of the selected card.
   */
  public selectOption(optionId: StartOptionId): void {
    if (this.isNavigating()) return;

    this.isNavigating.set(true);
    const targetRoute = getRouteForStartOption(optionId);

    this.router.navigate([targetRoute]).finally(() => {
      this.isNavigating.set(false);
    });
  }
}
